import React, { useState } from 'react';

import ConfirmDialog from '../ConfirmDialog';
import apiClient from '../../services/api';
import useResource from '../../services/useResource';
import { AudioAsset, MaybePaginated } from '../../types/api';
import { formatTime } from './formatTime';
import './DuplicatesView.css';

interface DuplicatesViewProps {
  onError: (message: string | null) => void;
}

interface DupGroup {
  key: string;
  filename: string;
  copies: AudioAsset[];
}

/**
 * Flat duplicate finder for the audio library. Two entries are
 * considered copies of the same file when original filename AND byte
 * size match — bulk imports and repeat uploads can land the same track
 * in several folders. Grouping is client-side over the full (small)
 * asset list; each row deletes one copy.
 */
export default function DuplicatesView({ onError }: DuplicatesViewProps) {
  const { data: assets, loading, setData } = useResource<AudioAsset[], MaybePaginated<AudioAsset>>(
    () => apiClient.get<MaybePaginated<AudioAsset>>('/audiolib/audio/'),
    [],
    { initial: [], transform: (raw) => (Array.isArray(raw) ? raw : raw.results || []) },
  );

  const groups: DupGroup[] = [];
  {
    const byKey = new Map<string, AudioAsset[]>();
    for (const a of assets) {
      const key = `${a.original_filename || a.name}|${a.size_bytes}`;
      const list = byKey.get(key) || [];
      list.push(a);
      byKey.set(key, list);
    }
    for (const [key, copies] of byKey) {
      if (copies.length > 1) {
        groups.push({ key, filename: copies[0].original_filename || copies[0].name, copies });
      }
    }
    groups.sort((x, y) => y.copies.length - x.copies.length);
  }

  const [pendingDelete, setPendingDelete] = useState<AudioAsset | null>(null);

  async function deleteCopy(asset: AudioAsset): Promise<void> {
    setPendingDelete(null);
    onError(null);
    try {
      await apiClient.delete(`/audiolib/audio/${asset.id}/`);
      setData((prev) => prev.filter((a) => a.id !== asset.id));
    } catch (err) {
      onError((err as Error).message || 'Delete failed');
    }
  }

  if (loading) return <p className="muted">Scanning for duplicates…</p>;
  if (groups.length === 0) {
    return (
      <div className="panel audio-library-empty">
        <p className="muted">No duplicates — every track in the library is unique.</p>
      </div>
    );
  }

  return (
    <>
    {pendingDelete && (
      <ConfirmDialog
        title={`Delete this copy of "${pendingDelete.name}"?`}
        message={(
          <p className="muted">
            Removes the copy in {pendingDelete.folder || 'the library root'}.
            Posts and projects that already used it keep their own copy.
          </p>
        )}
        onConfirm={() => deleteCopy(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />
    )}
    <ul className="audio-dup-groups">
      {groups.map((g) => (
        <li key={g.key} className="panel audio-dup-group">
          <div className="audio-dup-head">
            <span className="audio-dup-filename">{g.filename}</span>
            <span className="badge">{g.copies.length} copies</span>
          </div>
          <ul className="audio-dup-rows">
            {g.copies.map((a) => (
              <li key={a.id} className="audio-dup-row">
                <span className="audio-dup-name">
                  {a.is_favorite && <span className="audio-dup-fav" aria-label="favorite">★ </span>}
                  {a.name}
                </span>
                <span className="muted audio-dup-folder">{a.folder || 'Library (root)'}</span>
                <span className="muted audio-dup-duration">
                  {a.duration != null ? formatTime(a.duration) : '—'}
                </span>
                <button
                  type="button"
                  className="danger btn--sm"
                  onClick={() => setPendingDelete(a)}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
    </>
  );
}
